import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, Palette, ClipboardList, Handshake, HardHat, Coins, Building } from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  {
    id: 'architectural-design',
    icon: Building2,
    title: 'Architectural Design',
    shortDesc: 'Comprehensive design services from concept to construction documents.',
    fullDesc: 'From concept to construction documents, we provide comprehensive design services. Our team of highly skilled Architects brings years of combined experience, ensuring every project is executed with precision and passion. We create designs that are not just visually stunning but also functional and sustainable.',
    features: [
      'Conceptual Design & Planning',
      'Detailed Architectural Drawings',
      'Building Information Modeling (BIM)',
      '3D Visualization & Rendering',
      'Regulatory Compliance Design',
      'Sustainable Architecture Solutions'
    ]
  },
  {
    id: 'interior-design',
    icon: Palette,
    title: 'Interior Design',
    shortDesc: 'Creating spaces that complement architecture with comfort and style.',
    fullDesc: 'We create interior spaces that complement the architectural design, enhancing comfort and style. Our approach focuses on clean design, functional planning, and cost-friendly solutions, helping clients create comfortable and contemporary homes without compromising on quality.',
    features: [
      'Space Planning & Layout',
      'Material & Finish Selection',
      'Custom Furniture Design',
      'Lighting Design',
      'Color Consultation',
      'Turnkey Interior Solutions'
    ]
  },
  {
    id: 'project-management',
    icon: ClipboardList,
    title: 'Project Management Consulting',
    shortDesc: 'Expert project management ensuring timely and cost-effective delivery.',
    fullDesc: 'We oversee every stage of the project, ensuring timely and cost-effective completion. Our PMC services include expert guidance from start to finish, transparent developer selection, protecting society\'s interests, and rigorous timeline and cost control.',
    features: [
      'Feasibility Analysis',
      'Developer Selection & Tendering',
      'Cost Monitoring & Control',
      'Quality Assurance',
      'Timeline Management',
      'Stakeholder Communication'
    ]
  },
  {
    id: 'liaisoning',
    icon: Handshake,
    title: 'Liaisoning',
    shortDesc: 'Securing approvals from regulatory bodies and government authorities.',
    fullDesc: 'Critical role of securing necessary approvals between the project and external regulatory bodies, government authorities, and other stakeholders. We navigate the complex regulatory landscape to ensure all permissions and clearances are obtained efficiently.',
    features: [
      'Municipal Approvals (IOD/CC/OC)',
      'Environmental Clearances',
      'Fire Department NOC',
      'RERA Registration',
      'Building Plan Approvals',
      'Utility Connections'
    ]
  },
  {
    id: 'construction',
    icon: HardHat,
    title: 'Construction',
    shortDesc: 'Building structures for residential and commercial purposes.',
    fullDesc: 'Construction is the process where contractors build structures for residential or commercial purposes. We work with vetted contractors and maintain strict quality standards throughout the construction process, ensuring safety and structural integrity.',
    features: [
      'Contractor Selection & Management',
      'Quality Control & Inspection',
      'Safety Compliance',
      'Progress Monitoring',
      'Material Procurement',
      'Defect Rectification'
    ]
  },
  {
    id: 'finance-solutions',
    icon: Coins,
    title: 'Finance Solutions',
    shortDesc: 'Specialized funding methods for building projects.',
    fullDesc: 'Specialized funding methods used to cover the extensive costs of building projects from start to finish. We help secure financing through bank loans, sale of extra flats, member contributions, construction partner funding, and investor partnerships.',
    features: [
      'Bank Loan Assistance',
      'Financial Feasibility Analysis',
      'Funding Structure Planning',
      'Cash Flow Management',
      'Investor Relations',
      'Cost Optimization'
    ]
  },
  {
    id: 'redevelopment-advisory',
    icon: Building,
    title: 'Redevelopment Advisory',
    shortDesc: 'Expert guidance for housing societies on redevelopment processes.',
    fullDesc: 'Expert guidance for housing societies on self-redevelopment and 79A redevelopment processes. We help societies navigate the complex redevelopment journey, from initial feasibility to project completion, ensuring member interests are protected.',
    features: [
      'Feasibility Assessment',
      '79A Process Guidance',
      'Self-Redevelopment Consulting',
      'Developer Evaluation',
      'Member Consensus Building',
      'Regulatory Compliance'
    ]
  }
];

// Hero Section
const ServicesHero = () => {
  return (
    <section className="pt-[140px] pb-[120px] md:pt-[180px] bg-background" data-testid="services-hero">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Our Services</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Comprehensive<br />
            <span className="text-gold">Real Estate Solutions</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From architectural design to project completion, we offer end-to-end services 
            that transform your real estate vision into reality. Our expert team ensures 
            excellence at every stage.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Services Grid
const ServicesGrid = () => {
  return (
    <section className="arch-section bg-card" data-testid="services-grid">
      <div className="arch-container">
        <div className="space-y-0">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              id={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 py-16 border-b border-white/10 last:border-0"
              data-testid={`service-${service.id}`}
            >
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <service.icon className="w-10 h-10 text-gold" strokeWidth={1.5} />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    0{index + 1}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
                  {service.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {service.fullDesc}
                </p>
              </div>
              
              <div className="lg:pl-8">
                <h3 className="text-xs uppercase tracking-widest text-gold mb-6">What We Offer</h3>
                <ul className="space-y-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/80">
                      <span className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// PMC Services Detail
const PMCServicesDetail = () => {
  const pmcServices = [
    { title: 'Cost Monitoring', desc: 'Rigorous financial oversight to prevent cost overruns' },
    { title: 'Strategy', desc: 'Strategic planning for optimal project execution' },
    { title: 'Safety', desc: 'Compliance with all safety regulations and standards' },
    { title: 'Contract Admin', desc: 'Managing all contractual obligations and documentation' },
    { title: 'Quality', desc: 'Ensuring highest quality standards throughout' },
    { title: 'Communication', desc: 'Clear stakeholder communication at every stage' },
    { title: 'Technology', desc: 'Leveraging modern tools for project management' },
    { title: 'Planning & Scheduling', desc: 'Detailed timelines and milestone tracking' }
  ];

  return (
    <section className="arch-section bg-background" data-testid="pmc-services-detail">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">PMC Excellence</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
            Project Management Scope
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {pmcServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="p-6 border border-white/10 hover:border-gold/30 transition-colors group"
            >
              <h3 className="text-lg font-serif text-white mb-2 group-hover:text-gold transition-colors">
                {service.title}
              </h3>
              <p className="text-sm text-muted-foreground">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const ServicesCTA = () => {
  return (
    <section className="arch-section bg-card" data-testid="services-cta">
      <div className="arch-container text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Contact us today to discuss your project requirements and discover how our comprehensive 
            services can bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                className="bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
                data-testid="services-contact-btn"
              >
                Schedule Consultation
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="services-portfolio-btn"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Services Page
const ServicesPage = () => {
  return (
    <main data-testid="services-page">
      <ServicesHero />
      <ServicesGrid />
      <PMCServicesDetail />
      <ServicesCTA />
    </main>
  );
};

export default ServicesPage;
